import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Gift, Copy, Share2, Users, Sparkles, Check, Crown } from "lucide-react";
import { toast } from "sonner";

/**
 * Refer-a-friend widget.
 * Rules:
 *   Every 1 paid conversion  →  +30 days Premium credit (banked)
 *   Every 5th paid conversion →  +30 days Executive bonus (stacked)
 *
 * Shows: copy-link CTA, live stats, progress bar to next Executive bonus,
 * and a "Redeem" button once credit is earned.
 */
export default function ReferralWidget() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/referrals/me");
      setStats(data);
    } catch { /* no-op */ }
  };
  useEffect(() => { load(); }, []);

  if (!stats || !stats.code) return null;

  const url = `${window.location.origin}/?ref=${stats.code}`;
  const bonusEvery = stats.bonus_every_n || 5;
  const conversionsUntilBonus = stats.next_bonus_in ?? bonusEvery;
  const progress = ((bonusEvery - conversionsUntilBonus) / bonusEvery) * 100;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed — long-press to copy manually");
    }
  };

  const share = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({
        title: "FlowFinder — Trade Smarter",
        text: "I use FlowFinder for real-time stock scanning + AI-powered analysis. Join me →",
        url,
      });
    } catch { /* user cancelled */ }
  };

  const redeem = async () => {
    if (stats.credit_days <= 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/referrals/redeem");
      toast.success(`Applied ${data.days_applied} days of ${data.plan}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Redeem failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="card-surface p-5 relative overflow-hidden"
      data-testid="referral-widget"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{ background: "radial-gradient(circle at 100% 0%, rgba(0,255,156,0.20), transparent 55%)" }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Gift className="w-4 h-4 text-[var(--bull)]" />
          <div className="label-mono">Refer a friend</div>
        </div>
        <div className="text-white text-[15px] font-semibold tracking-tight leading-tight">
          Earn free months of FlowFinder
        </div>
        <div className="text-white/55 text-[12px] mt-1 leading-relaxed">
          Every friend who upgrades gets you <span className="text-[var(--bull)]">1 free month of Premium</span>.
          {" "}Every {bonusEvery}th unlocks a <span className="text-[#ffd166]">bonus month of Executive</span>.
        </div>

        {/* Share link */}
        <div className="mt-4 flex items-center gap-2">
          <div
            className="flex-1 min-w-0 px-3 py-2 rounded-full bg-white/[0.04] border border-white/10 text-[12px] font-mono text-white/75 truncate"
            title={url}
            data-testid="referral-link"
          >
            {url.replace(/^https?:\/\//, "")}
          </div>
          <button
            onClick={copy}
            className="btn-ghost inline-flex items-center gap-1.5 shrink-0"
            data-testid="referral-copy-btn"
            aria-label="Copy referral link"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-[var(--bull)]" /> : <Copy className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
          </button>
          <button
            onClick={share}
            className="btn-accent inline-flex items-center gap-1.5 shrink-0"
            data-testid="referral-share-btn"
            aria-label="Share referral link"
          >
            <Share2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Share</span>
          </button>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="rounded-xl border border-white/5 p-2.5 bg-white/[0.02]" data-testid="referral-stat-clicks">
            <div className="label-mono">Clicks</div>
            <div className="text-white font-mono text-lg tabular-nums font-semibold mt-0.5">{stats.clicks || 0}</div>
          </div>
          <div className="rounded-xl border border-white/5 p-2.5 bg-white/[0.02]" data-testid="referral-stat-signups">
            <div className="label-mono">Sign-ups</div>
            <div className="text-white font-mono text-lg tabular-nums font-semibold mt-0.5">{stats.signups || 0}</div>
          </div>
          <div className="rounded-xl border border-[var(--bull)]/30 p-2.5 bg-[var(--bull)]/[0.06]" data-testid="referral-stat-paid">
            <div className="label-mono">Upgrades</div>
            <div className="text-[var(--bull)] font-mono text-lg tabular-nums font-semibold mt-0.5">{stats.paid_conversions || 0}</div>
          </div>
        </div>

        {/* Next bonus progress */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-white/45 mb-1.5">
            <span className="inline-flex items-center gap-1">
              <Crown className="w-3 h-3 text-[#ffd166]" />
              Next Executive bonus
            </span>
            <span data-testid="referral-next-bonus">{conversionsUntilBonus} to go</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/[0.05] overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg, #00ff9c, #ffd166)",
              }}
              data-testid="referral-progress-bar"
            />
          </div>
        </div>

        {/* Credit balance + redeem */}
        {stats.credit_days > 0 ? (
          <div
            className="mt-4 flex items-center justify-between gap-2 px-3 py-3 rounded-2xl border border-[var(--bull)]/30 bg-[var(--bull)]/[0.06]"
            data-testid="referral-credit-block"
          >
            <div>
              <div className="text-white text-[13px] font-semibold tracking-tight">
                <Sparkles className="inline w-3 h-3 mb-0.5 mr-1 text-[var(--bull)]" />
                {stats.credit_days} days banked
              </div>
              <div className="text-white/55 text-[11px]">
                {stats.credit_months} month{stats.credit_months === 1 ? "" : "s"} of free access
              </div>
            </div>
            <button
              onClick={redeem}
              disabled={busy}
              className="btn-accent inline-flex items-center gap-1.5"
              data-testid="referral-redeem-btn"
            >
              {busy ? "Applying…" : "Redeem"}
            </button>
          </div>
        ) : (
          stats.paid_conversions > 0 && (
            <div className="mt-4 text-[11px] text-white/50 text-center">
              <Sparkles className="inline w-3 h-3 mb-0.5 mr-1 text-[var(--bull)]" />
              {stats.rewards_earned} reward{stats.rewards_earned === 1 ? "" : "s"} banked. Refer more to accumulate.
            </div>
          )
        )}

        {stats.conversion_rate > 0 && (
          <div className="mt-3 text-[11px] label-mono text-center" data-testid="referral-conversion-rate">
            <Users className="inline w-3 h-3 mb-0.5 mr-1" />
            {stats.conversion_rate}% conversion rate
          </div>
        )}
      </div>
    </div>
  );
}
